import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { cancelInProgressDataQueries } from './cancel-queries';
import { DataQueryMode, DEFAULT_DATA_QUERY_MODE } from './model';

export interface UseHandleQueryModeChangesProps {
  queryMode?: DataQueryMode;
  /**
   * Base of the query key used for all associated data queries.
   */
  baseQueryKey: QueryKey;
}

export interface UseHandleQueryModeChangesResponse {
  queryMode: DataQueryMode;
  cancelInProgressDataQueries: () => Promise<void>;
}

type DataQueryModeChangeActions = {
  cancel?: boolean;
  reset?: boolean;
};

/**
 * Hook that handles `queryMode`-related functionality needed when working with data queries in `DataQueriesProvider` including:
 * - Ensuring the `queryMode` is always set by falling back to a default when undefined.
 * - Handling side effects of the `queryMode` changing (e.g. cancelling queries when moving from active to suspended).
 */
export const useHandleQueryModeChanges = ({
  queryMode = DEFAULT_DATA_QUERY_MODE,
  baseQueryKey,
}: UseHandleQueryModeChangesProps): UseHandleQueryModeChangesResponse => {
  const queryClient = useQueryClient();
  const prevQueryMode = useRef<DataQueryMode>();

  useEffect(() => {
    async function handleQueryModeChange({ cancel = false, reset = false }: DataQueryModeChangeActions = {}) {
      if (cancel) {
        await cancelInProgressDataQueries(queryClient, baseQueryKey);
      }

      if (reset) {
        await queryClient.resetQueries({
          queryKey: baseQueryKey,
        });
      }
    }

    if (prevQueryMode.current === queryMode) {
      // Mode hasn't changed. Nothing useful to do.
      return;
    }

    const changeActions: DataQueryModeChangeActions = {};

    if (prevQueryMode.current === 'active' && queryMode !== 'active') {
      // Mode has changed from 'active' to a non-active mode. Cancel queries.
      changeActions.cancel = true;
    }
    if (prevQueryMode.current !== 'unmounted' && queryMode === 'unmounted') {
      // Mode has changed from another mode to `unmounted`. Reset queries to clear the cache.
      changeActions.reset = true;
    }

    // Update the ref to the latest value.
    prevQueryMode.current = queryMode;

    // Check if any of the actions are true.
    const hasChangeActions = Object.values(changeActions).some((action) => !!action);

    // Handle actions last because they're async.
    if (hasChangeActions) {
      // You cannot await inside useEffect and that's ok as long as you put it in a function like is done here.
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      handleQueryModeChange(changeActions);
    }
  }, [queryMode, queryClient, baseQueryKey]);

  // Very intentionally NOT using useCancelInProgressDataQueries to handle this because it can get
  // called a huge number of times with the current way our querying logic works and that combined with it
  // always calling useIsFetching causes performance issues.
  const cancelInProgressDataQueriesFn = useCallback(async () => {
    await cancelInProgressDataQueries(queryClient, baseQueryKey);
  }, [queryClient, baseQueryKey]);

  return {
    queryMode,
    cancelInProgressDataQueries: cancelInProgressDataQueriesFn,
  };
};
